import React, { useState } from 'react';
import {
  appointmentSlotHeight,
  isCompactAppointmentSlot,
  statusShortLabel,
} from '../../calendar/appointmentCellUtils';
import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';
import { FLAGS, FLAG_PRIORITY, PROVIDERS, STATUS } from '../../calendar/mockData';
import type { Appointment, FlagKey } from '../../calendar/types';
import { formatClockTime, formatTimeRange } from '../../calendar/timeUtils';
import { appointmentFamily, cellTone, TONE_STYLES } from '../../calendar/utils';

const FLAG_TONE_MAP: Record<string, keyof typeof TONE_STYLES> = {
  green: 'green',
  yellow: 'yellow',
  red: 'red',
  ctx: 'ctx',
};

// Real readiness chips (from the appointment's green flags) — not a hardcoded placeholder.
const READINESS_CHIPS: ReadonlyArray<{ flag: FlagKey; label: string }> = [
  { flag: 'elig_ok', label: 'Elig' },
  { flag: 'forms_ok', label: 'Forms' },
  { flag: 'confirmed', label: 'Conf' },
  { flag: 'reminded', label: 'Remind' },
];

export interface AppointmentCellProps {
  appt: Appointment;
  rowHeight: number;
  large?: boolean;
  onClick?: () => void;
}

export function AppointmentCell({ appt, rowHeight, large, onClick }: AppointmentCellProps) {
  const [hov, setHov] = useState(false);

  // A time held by an online booking request (spec 50 §3.4) — a striped block,
  // not a real appointment, until the request is booked or released.
  if (appt.held) {
    const h12h = appt.hour > 12 ? appt.hour - 12 : appt.hour;
    const per = appt.hour >= 12 ? 'PM' : 'AM';
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(ev) => { if (ev.key === 'Enter') onClick?.(); }}
        title={`Held · online request · ${appt.patient}`}
        className="relative overflow-hidden rounded-xl"
        style={{
          height: rowHeight * appt.duration - 5,
          border: '1px dashed var(--border-strong, #CFD5DF)',
          backgroundColor: 'var(--surface-sunken, #fafbfe)',
          backgroundImage:
            'repeating-linear-gradient(45deg, color-mix(in srgb, var(--text-muted) 12%, transparent) 0 6px, transparent 6px 12px)',
        }}
      >
        <div className={cn('flex h-full flex-col justify-center', large ? 'px-3' : 'px-2.5')}>
          <span className="inline-flex items-center gap-1.5 text-[10.5px] font-semibold uppercase tracking-[0.05em] text-[var(--text-muted)]">
            <Icon kind="clock" size={11} stroke={2} /> Held · online request
          </span>
          <span className="mt-0.5 truncate text-[12.5px] font-semibold text-[var(--text-secondary)]">{appt.patient}</span>
          <span className="font-[var(--font-mono)] text-[10px] text-[var(--text-muted)]">
            {String(h12h)}:00 {per}
          </span>
        </div>
      </div>
    );
  }

  const tone = cellTone(appt.flags);
  const tn = TONE_STYLES[tone];
  const st = STATUS[appt.status] ?? { label: 'Scheduled', tone: 'neutral' as const };
  const fam = appointmentFamily(appt.status);
  const prov = PROVIDERS.find((p) => p.id === appt.providerId);
  const pc = prov?.color ?? 'var(--ink-3)';
  const sorted = [...appt.flags].sort((a, b) => FLAG_PRIORITY.indexOf(a) - FLAG_PRIORITY.indexOf(b));
  const isAllClear = appt.flags.includes('all_clear');
  const nonClear = sorted.filter((f) => f !== 'all_clear');
  const endHour = appt.hour + appt.duration;
  const slotHeight = appointmentSlotHeight(rowHeight, appt.duration);
  const compact = !large && isCompactAppointmentSlot(rowHeight, appt.duration);
  const timeLabel = large
    ? formatTimeRange(appt.hour, endHour)
    : formatClockTime(appt.hour, compact ? { compact: true } : undefined);
  const readyLabel =
    tone === 'green' ? 'All ELVA checks passed' : tone === 'yellow' ? 'Needs attention' : 'Critical — review now';

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter') onClick?.();
      }}
      onMouseEnter={() => { setHov(true); }}
      onMouseLeave={() => { setHov(false); }}
      title={`${formatTimeRange(appt.hour, endHour)} · ${appt.patient}`}
      className={cn(
        'appointment-card relative cursor-pointer overflow-hidden rounded-xl transition-[transform,box-shadow,border-color] duration-[160ms] ease-[var(--ease-out)]',
        compact
          ? 'flex items-center gap-1 px-2 py-0 pl-2.5'
          : large
            ? 'px-[13px] py-[11px] pl-[17px]'
            : 'px-[11px] py-[9px] pl-4',
        hov && '-translate-y-px shadow-[var(--sh-2)] z-[5]',
        !hov && 'z-[1]',
      )}
      style={{
        height: slotHeight,
        background: fam.fill,
        border: `1px solid ${hov ? fam.rail : fam.border}`,
      }}
    >
      <span className="absolute bottom-0 left-0 top-0 w-[3.5px] rounded-l-xl" style={{ background: fam.rail }} />
      {compact ? (
        <>
          <span
            title={readyLabel}
            className="h-[6px] w-[6px] shrink-0 rounded-full"
            style={{ background: tn.fg, boxShadow: `0 0 0 2px color-mix(in srgb, ${tn.fg} 16%, transparent)` }}
          />
          <span className="shrink-0 font-[var(--font-mono)] text-[9.5px] font-medium text-[var(--ink-3)]">
            {timeLabel}
          </span>
          <span className="min-w-0 flex-1 truncate text-[12px] font-semibold tracking-[-0.01em] text-[var(--ink-0)]">
            {appt.patient}
          </span>
          <span
            className="shrink-0 rounded px-1 py-px text-[8.5px] font-bold uppercase tracking-[0.03em] text-white"
            style={{ background: fam.rail }}
          >
            {statusShortLabel(st.label)}
          </span>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between gap-1.5">
            <span className="inline-flex items-center gap-[7px]">
              <span
                title={readyLabel}
                className="h-[7px] w-[7px] shrink-0 rounded-full"
                style={{ background: tn.fg, boxShadow: `0 0 0 3px color-mix(in srgb, ${tn.fg} 16%, transparent)` }}
              />
              <span className="font-[var(--font-mono)] text-[10.5px] font-medium text-[var(--ink-3)]">
                {timeLabel}
              </span>
            </span>
            <span
              className="rounded-[5px] px-[7px] py-px text-[9.5px] font-semibold uppercase tracking-[0.04em] text-white"
              style={{ background: fam.rail }}
            >
              {slotHeight < 72 ? statusShortLabel(st.label) : st.label}
            </span>
          </div>
          <div
            className={cn(
              'truncate font-semibold tracking-[-0.012em] text-[var(--ink-0)]',
              large ? 'mt-[5px] text-sm' : 'mt-[3px] text-[13px]',
            )}
          >
            {appt.patient}
          </div>
          {large && (
            <div className="mt-[3px] flex items-center gap-1.5 overflow-hidden whitespace-nowrap text-[11px] text-[var(--ink-3)]">
              <span className="h-1.5 w-1.5 shrink-0 rounded-full" style={{ background: pc }} />
              <span className="font-medium text-[var(--ink-2)]">{prov?.name.split(' ').slice(-1)[0]}</span>
              <span className="text-[var(--ink-4)]">·</span>
              <span className="truncate text-[var(--ink-3)]">{appt.procedure}</span>
            </div>
          )}
          {slotHeight >= 72 ? (
            <div className={cn('flex items-center justify-between gap-1.5', large ? 'mt-auto pt-1.5' : 'mt-1.5')}>
              <div className="flex items-center gap-1">
                {(large && isAllClear ? [] : (large ? nonClear : sorted).slice(0, large ? 6 : 3)).map((fk) => {
                  const f = FLAGS[fk];
                  if (!f) return null;
                  const ft = TONE_STYLES[FLAG_TONE_MAP[f.tone] ?? 'ctx'];
                  return (
                    <span
                      key={fk}
                      title={f.label}
                      className="inline-flex h-[17px] w-[17px] items-center justify-center rounded-[5px]"
                      style={{ background: ft.tint, color: ft.fg }}
                    >
                      <Icon kind={f.icon} size={9.5} stroke={2} />
                    </span>
                  );
                })}
                {large &&
                  isAllClear &&
                  READINESS_CHIPS.filter((c) => appt.flags.includes(c.flag)).map(({ flag, label: l }) => (
                    <span
                      key={flag}
                      className="inline-flex h-4 items-center gap-[3px] rounded bg-[var(--success-tint)] px-[5px] text-[9.5px] font-bold text-[var(--success)]"
                    >
                      <Icon kind="check" size={8} stroke={3} />
                      {l}
                    </span>
                  ))}
              </div>
              <span className="font-[var(--font-mono)] text-[11px] font-semibold text-[var(--ink-2)]">
                ${appt.production.toLocaleString()}
              </span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
